import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Icon } from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { MapPin, Navigation } from 'lucide-react';

/* ── Icones markers ─────────────────────────────────────── */
const COLORS = { open: '#00c9a7', closed: '#f0564a', uncertain: '#f5a623' };

const createShopIcon = (status) => new Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 44" width="34" height="44">
      <defs>
        <filter id="sh" x="-30%" y="-20%" width="160%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" flood-opacity="0.28"/>
        </filter>
      </defs>
      <path d="M17 2C9.82 2 4 7.82 4 15c0 9.5 13 27 13 27s13-17.5 13-27C30 7.82 24.18 2 17 2z"
        fill="${COLORS[status] || COLORS.closed}" filter="url(#sh)"/>
      <circle cx="17" cy="15" r="6" fill="white" opacity="0.96"/>
      <circle cx="17" cy="15" r="2.8" fill="${COLORS[status] || COLORS.closed}"/>
    </svg>
  `)}`,
  iconSize:    [34, 44],
  iconAnchor:  [17, 44],
  popupAnchor: [0, -46],
});

const userIcon = new Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
      <circle cx="16" cy="16" r="15" fill="#3b82f6" fill-opacity="0.16" stroke="#3b82f6" stroke-width="1.5"/>
      <circle cx="16" cy="16" r="8" fill="#3b82f6"/>
      <circle cx="16" cy="16" r="4" fill="white"/>
    </svg>
  `)}`,
  iconSize:   [32, 32],
  iconAnchor: [16, 16],
});

/* ── Centre intelligent ─────────────────────────────────── */
const getSmartCenter = (userLoc, shops) => {
  if (userLoc?.lat && userLoc?.lng)
    return { center: [userLoc.lat, userLoc.lng], zoom: 14, source: 'user' };
  const s = shops?.find(s => s.latitude && s.longitude);
  if (s) return { center: [parseFloat(s.latitude), parseFloat(s.longitude)], zoom: 13, source: 'shop' };
  return { center: [36.7372, 3.0865], zoom: 12, source: 'fallback' };
};

/* ── Controleur carte ───────────────────────────────────── */
const MapController = ({ userLoc, onUserMoved, recenterSignal }) => {
  const map        = useMap();
  const initRef    = useRef(false);

  useEffect(() => {
    if (!userLoc || initRef.current) return;
    initRef.current = true;
    map.setView([userLoc.lat, userLoc.lng], 14, { animate: true });
  }, [userLoc, map]);

  useEffect(() => {
    if (!recenterSignal || !userLoc) return;
    map.setView([userLoc.lat, userLoc.lng], 14, { animate: true, duration: 0.6 });
  }, [recenterSignal, userLoc, map]);

  useMapEvents({ dragend: () => onUserMoved(true) });
  return null;
};

/* ── Marker utilisateur ─────────────────────────────────── */
const UserMarker = ({ userLoc }) => {
  if (!userLoc) return null;
  return (
    <Marker position={[userLoc.lat, userLoc.lng]} icon={userIcon}>
      <Popup>
        <div style={{ textAlign: 'center', padding: '0.2rem 0', fontWeight: 700, fontSize: '0.85rem' }}>
          Vous etes ici
        </div>
      </Popup>
    </Marker>
  );
};

/* Libelle statut sans emoji */
const statusLabel = s => s === 'open' ? 'Ouvert' : s === 'uncertain' ? 'Incertain' : 'Ferme';

/* ══════════════════════════════════════════════════════════ */
const MapView = ({ shops = [], center, zoom }) => {
  const navigate                        = useNavigate();
  const { location }                    = useLocation();
  const [hasMoved, setHasMoved]         = useState(false);
  const [recenterSignal, setRecenter]   = useState(0);

  const userLoc    = location || null;
  const smart      = getSmartCenter(center ? null : userLoc, shops);
  const initCenter = center || smart.center;
  const initZoom   = zoom   || smart.zoom;

  const handleRecenter = useCallback(() => {
    if (!userLoc) return;
    setHasMoved(false);
    setRecenter(s => s + 1);
  }, [userLoc]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        center={initCenter}
        zoom={initZoom}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: 'inherit' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController userLoc={userLoc} onUserMoved={setHasMoved} recenterSignal={recenterSignal}/>
        <UserMarker userLoc={userLoc}/>

        {shops.map(shop => {
          const lat = parseFloat(shop.latitude);
          const lng = parseFloat(shop.longitude);
          if (isNaN(lat) || isNaN(lng)) return null;
          const color = COLORS[shop.status] || COLORS.closed;
          return (
            <Marker
              key={shop.id}
              position={[lat, lng]}
              icon={createShopIcon(shop.status)}
              eventHandlers={{ click: () => navigate(`/shop/${shop.id}`) }}
            >
              <Popup>
                {/* Popup neumorphique — styles inline car dans Leaflet */}
                <div style={{
                  minWidth: 175, padding: '0.1rem 0',
                  fontFamily: "'DM Sans', -apple-system, sans-serif",
                }}>
                  <div style={{ fontWeight: 800, fontSize: '0.93rem', marginBottom: '0.45rem', color: '#1a2340', lineHeight: 1.3 }}>
                    {shop.name}
                  </div>

                  {/* Badge statut */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.32rem',
                    padding: '0.28rem 0.65rem',
                    borderRadius: '999px',
                    background: shop.status === 'open'
                      ? 'linear-gradient(135deg,#00c9a7,#00b396)'
                      : shop.status === 'uncertain'
                      ? 'linear-gradient(135deg,#f5a623,#e8920f)'
                      : 'linear-gradient(135deg,#f0564a,#dc2626)',
                    marginBottom: '0.5rem',
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: 'rgba(255,255,255,.85)',
                      animation: shop.status !== 'closed' ? 'pulseDot 2.2s ease infinite' : 'none',
                    }}/>
                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff' }}>
                      {statusLabel(shop.status)}
                    </span>
                  </div>

                  {/* Adresse */}
                  {shop.address && (
                    <div style={{ fontSize: '0.76rem', color: '#4a5470', lineHeight: 1.4, marginBottom: '0.55rem', display: 'flex', gap: '0.3rem' }}>
                      <MapPin size={11} style={{ flexShrink: 0, marginTop: 2, color: color }}/>
                      {shop.address}
                    </div>
                  )}

                  {/* Telephone */}
                  {shop.phone && shop.showPhone !== false && (
                    <a href={`tel:${shop.phone}`}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.76rem', color: '#00c9a7', fontWeight: 700, textDecoration: 'none', marginBottom: '0.5rem' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <span style={{ fontSize: '0.72rem' }}>{shop.phone}</span>
                    </a>
                  )}

                  {/* CTA */}
                  <button
                    onClick={() => navigate(`/shop/${shop.id}`)}
                    style={{
                      width: '100%', padding: '0.5rem',
                      borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(145deg,#00d9b4,#009e85)',
                      color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: '2px 2px 8px rgba(0,158,133,.40)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                    }}
                  >
                    <Navigation size={13}/> Voir le magasin
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* ── Bouton recentrage ── */}
      {userLoc && hasMoved && (
        <button
          onClick={handleRecenter}
          aria-label="Revenir a ma position"
          style={{
            position: 'absolute', bottom: 20, right: 12, zIndex: 1000,
            width: 44, height: 44,
            borderRadius: '50%',
            background: 'var(--bg, #e8edf5)',
            border: 'none',
            boxShadow: '4px 4px 12px rgba(163,177,198,.55),-3px -3px 10px rgba(255,255,255,.75)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.18s',
            color: '#1b2a6b',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          onMouseDown={e => e.currentTarget.style.boxShadow = 'inset 3px 3px 8px rgba(163,177,198,.55),inset -3px -3px 8px rgba(255,255,255,.70)'}
          onMouseUp={e => e.currentTarget.style.boxShadow = '4px 4px 12px rgba(163,177,198,.55),-3px -3px 10px rgba(255,255,255,.75)'}
        >
          <MapPin size={19} strokeWidth={2.5}/>
        </button>
      )}

      {/* Indicateur geoloc manquante */}
      {!userLoc && smart.source === 'shop' && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1000,
          background: 'var(--bg, #e8edf5)',
          boxShadow: '4px 4px 12px rgba(163,177,198,.50),-3px -3px 8px rgba(255,255,255,.70)',
          borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 600, color: 'var(--text)',
          padding: '0.32rem 0.9rem',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          <MapPin size={11} strokeWidth={2.5}/> Activez la geolocalisation
        </div>
      )}
    </div>
  );
};

export default MapView;